import React, { useEffect, useRef, useState } from "react";
import { Modal, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import axiosInstance from "../../utils/axiosInstance";
import { useDispatch } from "react-redux";
import { setWishlistItems } from "../../redux/wishlistSlice";
import CountryCodeSelect from "../../../../Utils/CountryCodeSelect";
import { getCountryPhoneConfig, validateCountryPhoneNumber } from "../../../../Utils/countries";
import "./login.css";

const getProductImage = (product) =>
  product.images?.[0]?.image?.image || product.product?.image?.image || "";

const normalizeWishlistItem = (item) => {
  const variant = item.varient || {};
  return {
    id: variant.id,
    wishlistId: item.id,
    name: variant.name,
    slug: item.slug || variant.slug,
    image: getProductImage(variant),
    type: item.type === "rental" || item.type === "rent" ? "rent" : "buy",
    sale_price: variant.prices?.sale_price,
    sale_offer_price: variant.prices?.sale_offer_price,
    rental_price: variant.prices?.rental_price,
    offerPrice:
      item.type === "rental" || item.type === "rent"
        ? variant.prices?.rental_price
        : variant.prices?.sale_offer_price || variant.prices?.sale_price,
  };
};

function LoginModal({ show, onHide, onLoginSuccess }) {
  const dispatch = useDispatch();
  const inputRef = useRef(null);
  const otpRefs = useRef([...Array(6)].map(() => React.createRef()));
  const [step, setStep] = useState("contact");
  const [contact, setContact] = useState("");
  const [countryCode, setCountryCode] = useState("+1");
  const [otpDigits, setOtpDigits] = useState(["", "", "", "", "", ""]);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loginData, setLoginData] = useState(null);
  const [agree, setAgree] = useState(false);
  const [clientName, setClientName] = useState("RentExcel");

  const phoneConfig = getCountryPhoneConfig(countryCode);

  useEffect(() => {
    const fetchSiteData = async () => {
      try {
        const res = await axiosInstance.get("sitedata/site-config/");
        if (res?.data?.tenant?.company_name) {
          setClientName(res.data.tenant.company_name);
        }
      } catch (err) {
        console.warn("Failed to fetch site config for client name", err);
      }
    };
    fetchSiteData();
  }, []);

  useEffect(() => {
    if (show) setTimeout(() => inputRef.current?.focus(), 120);
  }, [show]);

  const reset = () => {
    setStep("contact");
    setContact("");
    setCountryCode("+1");
    setOtpDigits(["", "", "", "", "", ""]);
    setMessage("");
    setError("");
    setLoading(false);
    setName("");
    setEmail("");
    setLoginData(null);
    setAgree(false);
  };

  const close = () => { reset(); onHide?.(); };

  const sendOtp = async () => {
    const nextContact = contact.trim();
    if (/^\d/.test(nextContact)) {
      const phoneError = validateCountryPhoneNumber(nextContact, countryCode);
      if (phoneError) {
        setError(phoneError);
        return;
      }
    } else {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(nextContact)) {
        setError("Enter a valid email address.");
        return;
      }
    }
    setLoading(true);
    setError("");
    setMessage("");
    try {
      const formattedContact = nextContact;

      const { data } = await axiosInstance.post("accounts/validate_contact/", {
        contact: formattedContact,
        role: "Customer",
        country_code: countryCode || "+1",
      });
      setMessage(data?.message || `OTP sent to ${nextContact}`);
      toast.success(data?.message || "OTP sent");
      setStep("otp");
      setOtpDigits(["", "", "", "", "", ""]);
      setTimeout(() => otpRefs.current[0]?.current?.focus(), 120);
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || "Unable to send OTP.");
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (overrideDigits) => {
    const digits = Array.isArray(overrideDigits) ? overrideDigits : otpDigits;
    const otp = digits.join("");
    if (otp.length !== 6) { setError("Enter the 6-digit OTP."); return; }
    setLoading(true);
    setError("");
    try {
      const trimmedContact = contact.trim();
      const formattedContact = trimmedContact;

      const { data } = await axiosInstance.post("accounts/verify_otp/", {
        contact: formattedContact,
        otp,
        country_code: countryCode || "+1",
      });
      if (data?.token) localStorage.setItem("token", data.token);
      if (data?.user_id) localStorage.setItem("userid", data.user_id);
      if (data?.store_id) localStorage.setItem("store_id", data.store_id);

      if (!data?.name) {
        setLoginData(data);
        setStep("name");
      } else {
        localStorage.setItem("name", data.name);
        if (data.user_id) {
          try {
            const res = await axiosInstance.get("catlog/wishlists/", {
              params: { user: data.user_id },
            });
            const results = Array.isArray(res.data?.results) ? res.data.results : [];
            const normalizedItems = results.map(normalizeWishlistItem).filter((item) => item.id);
            dispatch(setWishlistItems(normalizedItems));
          } catch (err) {
            console.error("Failed to fetch wishlist", err);
          }
        }
        window.dispatchEvent(new Event("authChange"));
        onLoginSuccess?.(data);
        toast.success("Logged in successfully");
        close();
      }
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || "Invalid OTP.");
    } finally {
      setLoading(false);
    }
  };

  const saveName = async () => {
    if (!name.trim()) {
      setError("Name is required");
      return;
    }
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError("Valid email is required");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const userId = localStorage.getItem("userid");
      if (userId) {
        await axiosInstance.patch(`user_data/users/${userId}/`, {
          full_name: name.trim(),
          email: email.trim()
        });
      }
      localStorage.setItem("name", name.trim());
      if (userId) {
        try {
          const res = await axiosInstance.get("catlog/wishlists/", {
            params: { user: userId },
          });
          const results = Array.isArray(res.data?.results) ? res.data.results : [];
          const normalizedItems = results.map(normalizeWishlistItem).filter((item) => item.id);
          dispatch(setWishlistItems(normalizedItems));
        } catch (err) {
          console.error("Failed to fetch wishlist", err);
        }
      }
      window.dispatchEvent(new Event("authChange"));
      onLoginSuccess?.({ ...loginData, name: name.trim(), email: email.trim() });
      toast.success("Logged in successfully");
      close();
    } catch (err) {
      setError(
        err.response?.data?.error ||
        err.response?.data?.message ||
        "Failed to save name."
      );
    } finally {
      setLoading(false);
    }
  };

  const updateOtpDigit = (value, index) => {
    if (loading) return;
    let nextDigits = [...otpDigits];
    if (value.length > 1) {
      const cleaned = value.replace(/\D/g, "").slice(0, 6);
      if (!cleaned) return;
      nextDigits = ["", "", "", "", "", ""];
      for (let i = 0; i < cleaned.length; i++) nextDigits[i] = cleaned[i];
      setOtpDigits(nextDigits);
      if (cleaned.length === 6) {
        otpRefs.current[5]?.current?.focus();
        verifyOtp(nextDigits);
      } else {
        otpRefs.current[cleaned.length]?.current?.focus();
      }
      return;
    }

    if (!/^\d?$/.test(value)) return;
    nextDigits[index] = value;
    setOtpDigits(nextDigits);
    if (value && index < 5) otpRefs.current[index + 1]?.current?.focus();
    if (nextDigits.join("").length === 6) {
      verifyOtp(nextDigits);
    }
  };

  const handleOtpKeyDown = (event, index) => {
    if (event.key === "Enter") {
      verifyOtp();
    } else if (event.key === "Backspace" && !otpDigits[index] && index > 0) {
      otpRefs.current[index - 1]?.current?.focus();
    }
  };

  return (
    <Modal show={show} onHide={close} centered className="template-construction" dialogClassName="lm-dialog">
      <Modal.Header closeButton className="template-construction lm-header">
        <Modal.Title className="template-construction lm-title">
          {step === "contact" ? "Sign In" : step === "name" ? "Complete Profile" : "Verify OTP"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="template-construction lm-body">
        {step === "contact" && (
          <>
            <p className="lm-sub">Enter your phone number or email to continue.</p>
            <label className="lm-label" style={{ display: "block", width: "100%" }}>
              Phone number or Email
              <div style={{ display: "flex", gap: "8px", alignItems: "stretch", marginTop: "4px" }}>
                <CountryCodeSelect
                  value={countryCode}
                  onChange={(dialCode) => {
                    setCountryCode(dialCode);
                    const nextConfig = getCountryPhoneConfig(dialCode);
                    if (/^\d/.test(contact)) {
                      setContact((prev) => prev.slice(0, nextConfig.maxLength));
                    }
                  }}
                />
                <input
                  ref={inputRef}
                  className="lm-input"
                  style={{ flex: 1, margin: 0 }}
                  type={/^\d/.test(contact) || contact === "" ? "tel" : "email"}
                  inputMode={/^\d/.test(contact) || contact === "" ? "numeric" : "email"}
                  placeholder={phoneConfig.placeholder ? `${phoneConfig.placeholder} or email` : "10-digit number or email address"}
                  value={contact}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val.length > 0 && /^\d/.test(val)) {
                      setContact(val.replace(/\D/g, "").slice(0, phoneConfig.maxLength));
                    } else {
                      setContact(val);
                    }
                    setError("");
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      e.stopPropagation();
                      if (agree) sendOtp();
                    }
                  }}
                />
              </div>
            </label>
          </>
        )}
        {step === "otp" && (
          <>
            <p className="lm-sub">{message || `OTP sent to ${/^\d/.test(contact) ? `${countryCode} ${contact}` : contact}`}</p>
            <div className="lm-otp-row" aria-label="OTP digits">
              {otpDigits.map((digit, index) => (
                <input
                  key={index}
                  ref={otpRefs.current[index]}
                  className="lm-otp-box"
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => updateOtpDigit(e.target.value, index)}
                  onKeyDown={(e) => handleOtpKeyDown(e, index)}
                />
              ))}
            </div>
            <button
              className="lm-link"
              type="button"
              onClick={() => { setStep("contact"); setOtpDigits(["", "", "", "", "", ""]); setError(""); }}
            >
              ← Change number
            </button>
          </>
        )}

        {step === "name" && (
          <>
            <p className="lm-sub">Complete your profile</p>
            <label className="lm-label">
              Full Name
              <input
                className="lm-input"
                type="text"
                placeholder="Enter your name"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setError("");
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    e.stopPropagation();
                    saveName();
                  }
                }}
              />
            </label>
            <label className="lm-label mt-2">
              Email Address
              <input
                className="lm-input"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError("");
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    e.stopPropagation();
                    saveName();
                  }
                }}
              />
            </label>
            {error && <p className="lm-error mt-2">{error}</p>}
            <button
              className="lm-submit mt-3"
              type="button"
              onClick={saveName}
              disabled={loading || !name.trim() || !email.trim()}
            >
              {loading ? <Spinner animation="border" size="sm" /> : "Save"}
            </button>
          </>
        )}

        {step !== "name" && (
          <>
            {error && <p className="lm-error">{error}</p>}
            <button
              className="lm-submit"
              type="button"
              onClick={step === "contact" ? sendOtp : verifyOtp}
              disabled={step === "contact" ? (loading || !agree) : loading}
            >
              {loading
                ? <Spinner animation="border" size="sm" />
                : step === "contact" ? "Send OTP" : "Verify & Login"}
            </button>
            {step === "contact" && (
              <div className="mt-3" style={{ fontSize: "12px", color: "#6c757d", display: "flex", alignItems: "flex-start", gap: "10px", textAlign: "left", width: "100%" }}>
                <input type="checkbox" id="agreeCheckbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} style={{ marginTop: "2px", cursor: "pointer", width: "16px", height: "16px", flexShrink: 0 }} />
                <label
                  htmlFor="agreeCheckbox"
                  style={{ cursor: "pointer", margin: 0, lineHeight: "1.4" }}
                >
                  By checking this box, you consent to receive SMS text messages from{" "}
                  {clientName} for account verification, login authentication, and security
                  notifications. Message frequency varies. Msg & data rates may apply.
                  Reply STOP to cancel, HELP for help. Consent is not a condition of
                  purchase or use. See our{" "}
                  <a
                    href="/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Privacy Policy
                  </a>{" "}
                  and{" "}
                  <a
                    href="/terms-of-service"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Terms of Use
                  </a>
                  .
                </label>
              </div>
            )}
          </>
        )}
      </Modal.Body>
    </Modal>
  );
}

export default LoginModal;